import React, { useEffect, useRef } from "react";
import cities from "../data/cities";
import geography from "../data/geographyData";
import TopBar from "../components/TopBar";
import { trackEvent } from "../utils/analytics";

export default function GameScreen({
  puzzle,
  game,
  setGame,
  onFinish,
  goBack,
  goToResults,
  onStats,
  openHowToPlay
}) {
  const timeoutsRef = useRef([]);
  const initialClueCountRef = useRef(null);

  const guess = game?.guess || "";
  const guesses = game?.guesses || [];
  const gameOver = game?.gameOver || false;
  const filtered = game?.filtered || [];
  const status = game?.status || "";

  const wrongGuesses = guesses.filter((g) => !g.correct).length;
  const clueLevel = game?.showAllClues
    ? puzzle?.clues?.length || 0
    : Math.min(wrongGuesses, 5);

  // Store how many clues were active when this component mounted
  if (initialClueCountRef.current === null) {
    initialClueCountRef.current = clueLevel;
  }

  // Sanitize pre-existing guesses on mount & clean up timeouts on unmount
  useEffect(() => {
    const currentTimeouts = timeoutsRef.current;

    setGame((prev) => {
      if (!prev?.guesses?.length) return prev;
      return {
        ...prev,
        guesses: prev.guesses.map((g) => ({
          ...g,
          revealStage: 4 // Neutralizes flip animations for previous guesses
        }))
      };
    });

    return () => {
      currentTimeouts.forEach(clearTimeout);
    };
  }, [setGame]);

  const addTimeout = (fn, delay) => {
    const id = setTimeout(fn, delay);
    timeoutsRef.current.push(id);
  };

  const getTileBg = (stage, currentStage, statusType) => {
    if (currentStage < stage) return "#f1f5f9"; // Unrevealed tile
    if (statusType === "correct") return "#6aaa64"; // Wordle Green
    if (statusType === "bordering") return "#c9b458"; // Wordle Yellow
    return "#D8DDE3"; // grey
  };

  const updateGame = (updates) => {
    setGame((prev) => ({
      guess: "",
      guesses: [],
      gameOver: false,
      filtered: [],
      status: "",
      ...prev,
      ...updates
    }));
  };

  // SOFT, ATMOSPHERIC BACKGROUND CLOUDS
  const CloudStyles = (
    <style>{`
      @keyframes floatCloudLeft {
        0% { transform: translateX(0px); }
        50% { transform: translateX(25px); }
        100% { transform: translateX(0px); }
      }
      @keyframes floatCloudRight {
        0% { transform: translateX(0px); }
        50% { transform: translateX(-25px); }
        100% { transform: translateX(0px); }
      }

      /* SOFT SOFTENED CLOUD BASE */
      .cloud-shape {
        position: absolute;
        background: rgba(255, 255, 255, 0.45);
        border-radius: 60px;
        pointer-events: none;
        filter: blur(14px);
        z-index: 0;
      }

      .cloud-shape::before,
      .cloud-shape::after {
        content: '';
        position: absolute;
        background: rgba(255, 255, 255, 0.45);
        border-radius: 50%;
      }

      .cloud-shape::before {
        width: 55%;
        height: 120%;
        top: -50%;
        left: 12%;
      }

      .cloud-shape::after {
        width: 48%;
        height: 100%;
        top: -40%;
        right: 12%;
      }

      .cloud1 {
        left: -30px;
        top: 70px;
        width: 220px;
        height: 65px;
        background: rgba(255, 255, 255, 0.50);
        filter: blur(16px);
        animation: floatCloudLeft 10s ease-in-out infinite;
      }

      .cloud2 {
        right: -35px;
        top: 160px;
        width: 240px;
        height: 70px;
        background: rgba(255, 255, 255, 0.40);
        filter: blur(18px);
        animation: floatCloudRight 12s ease-in-out infinite;
      }

      .cloud3 {
        left: -40px;
        top: 450px;
        width: 210px;
        height: 60px;
        background: rgba(255, 255, 255, 0.45);
        filter: blur(15px);
        animation: floatCloudLeft 13s ease-in-out infinite;
      }

      .cloud4 {
        right: -35px;
        top: 630px;
        width: 230px;
        height: 65px;
        background: rgba(255, 255, 255, 0.35);
        filter: blur(16px);
        animation: floatCloudRight 11s ease-in-out infinite;
      }

      @keyframes fadeInClue {
        from {
          opacity: 0;
          transform: translateY(-6px);
        }
        to {
          opacity: 1;
          transform: translateY(0);
        }
      }

      @keyframes flipTile {
        0% {
          transform: rotateX(0deg);
          animation-timing-function: ease-in;
        }
        50% {
          transform: rotateX(-90deg);
          animation-timing-function: ease-out;
        }
        100% {
          transform: rotateX(0deg);
        }
      }

      .flip-active {
        animation: flipTile 0.8s forwards;
        transition: background-color 0s 0.4s;
        will-change: transform;
      }

      .clue-item {
        animation: fadeInClue 0.4s ease-out forwards;
      }
    `}</style>
  );

  // LOADING SCREEN WITH CLOUDS
  if (!puzzle) {
    return (
      <div style={styles.page}>
        {CloudStyles}
        <div className="cloud-shape cloud1"></div>
        <div className="cloud-shape cloud2"></div>
        <div className="cloud-shape cloud3"></div>
        <div className="cloud-shape cloud4"></div>
        <div style={styles.loading}>Loading...</div>
      </div>
    );
  }

  const handleInput = (text) => {
    updateGame({ guess: text });

    if (!text) {
      updateGame({ filtered: [] });
      return;
    }

    const query = text.toLowerCase();

    const exactCity = cities.filter(
      (c) => c.name.toLowerCase() === query
    );

    const startsCity = cities.filter(
      (c) =>
        c.name.toLowerCase().startsWith(query) &&
        !exactCity.includes(c)
    );

    const startsCountry = cities.filter(
      (c) =>
        c.country.toLowerCase().startsWith(query) &&
        !exactCity.includes(c) &&
        !startsCity.includes(c)
    );

    const includesCity = cities.filter(
      (c) =>
        c.name.toLowerCase().includes(query) &&
        !exactCity.includes(c) &&
        !startsCity.includes(c) &&
        !startsCountry.includes(c)
    );

    const includesCountry = cities.filter(
      (c) =>
        c.country.toLowerCase().includes(query) &&
        !exactCity.includes(c) &&
        !startsCity.includes(c) &&
        !startsCountry.includes(c) &&
        !includesCity.includes(c)
    );

    const results = [
      ...exactCity,
      ...startsCity,
      ...startsCountry,
      ...includesCity,
      ...includesCountry
    ];

    updateGame({ filtered: results });
  };

  const getGeo = (country) => {
    return (
      geography[country] || {
        continent: "Unknown",
        borders: []
      }
    );
  };

  const submitGuess = (cityObj) => {
    if (!cityObj || gameOver) return;

    const isCorrectCity =
      cityObj.name.toLowerCase() === puzzle.name.toLowerCase();

    const correctGeo = getGeo(puzzle.country);
    const guessGeo = getGeo(cityObj.country);

    const isSameCountry = cityObj.country === puzzle.country;
    const isBordering = correctGeo.borders.includes(cityObj.country);

    const normalize = (c) =>
      Array.isArray(c.continent) ? c.continent : [c.continent];

    const guessContinents = normalize(guessGeo);
    const correctContinents = normalize(correctGeo);

    const isSameContinent = guessContinents.some((c) =>
      correctContinents.includes(c)
    );

    const guessEntry = {
      name: cityObj.name,
      country: cityObj.country,
      flag: cityObj.flag || "",
      continent: guessGeo.continent,
      correct: isCorrectCity,
      cityStatus: isCorrectCity ? "correct" : "wrong",
      countryStatus: isSameCountry
        ? "correct"
        : isBordering
        ? "bordering"
        : "wrong",
      continentStatus: isSameContinent ? "correct" : "wrong",
      revealStage: 0
    };

    const updatedGuesses = [...guesses, guessEntry];

    trackEvent("guess_submitted", {
      guess_number: updatedGuesses.length,
      correct: isCorrectCity,
      country_guessed: cityObj.country
    });

    const isGameEnding = isCorrectCity || updatedGuesses.length >= 6;
    let finalStatus = "❌ Not quite — try again";
    let finalOutcome = "";

    if (isCorrectCity) {
      finalStatus = `🎉 Correct! You landed in ${puzzle.name}`;
      finalOutcome = "win";
    } else if (updatedGuesses.length >= 6) {
      finalStatus = `❌ Unlucky - today's destination was ${puzzle.name}`;
      finalOutcome = "lose";
    }

    // Immediately update guesses and clear input/status while animation plays
    setGame((prev) => ({
      ...prev,
      guess: "",
      filtered: [],
      guesses: updatedGuesses,
      status: ""
    }));

    // Stage 1 reveal animation
    addTimeout(() => {
      setGame((prev) => ({
        ...prev,
        guesses: prev.guesses.map((g, i) =>
          i === updatedGuesses.length - 1 ? { ...g, revealStage: 1 } : g
        )
      }));
    }, 300);

    // Stage 2 reveal animation
    addTimeout(() => {
      setGame((prev) => ({
        ...prev,
        guesses: prev.guesses.map((g, i) =>
          i === updatedGuesses.length - 1 ? { ...g, revealStage: 2 } : g
        )
      }));
    }, 750);

    // Stage 3 reveal animation
    addTimeout(() => {
      setGame((prev) => ({
        ...prev,
        guesses: prev.guesses.map((g, i) =>
          i === updatedGuesses.length - 1 ? { ...g, revealStage: 3 } : g
        )
      }));
    }, 1250);

    // Reset active reveal stage AND reveal status/gameOver after animations finish
    addTimeout(() => {
      setGame((prev) => ({
        ...prev,
        status: finalStatus,
        gameOver: isGameEnding,
        outcome: finalOutcome,
        guesses: prev.guesses.map((g, i) =>
          i === updatedGuesses.length - 1 ? { ...g, revealStage: 4 } : g
        )
      }));
    }, 2050);

    if (isGameEnding) {
      addTimeout(() => {
        const completeGuessesArray = updatedGuesses.map((g, i) =>
          i === updatedGuesses.length - 1 ? { ...g, revealStage: 4 } : g
        );

        onFinish({
          puzzleCity: puzzle.name,
          puzzleFlag: puzzle.flag || "",
          puzzle,
          guesses: completeGuessesArray,
          status: finalStatus,
          outcome: finalOutcome
        });
      }, 2100);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      if (filtered.length === 0) return;
      submitGuess(filtered[0]);
    }
  };

  const departureCityObj = cities.find((c) => c.name === puzzle.departure);

  return (
    <div style={styles.page}>
      {CloudStyles}

      {/* VISIBLE BACKGROUND CLOUDS */}
      <div className="cloud-shape cloud1"></div>
      <div className="cloud-shape cloud2"></div>
      <div className="cloud-shape cloud3"></div>
      <div className="cloud-shape cloud4"></div>

      <TopBar onBack={goBack} onHelp={openHowToPlay} onStats={onStats} />

      <div style={styles.container}>
        {/* TOP CLUE CARD WITH BOLD BLACK BORDER */}
        <div style={styles.card}>
          <h3 style={{ margin: "0 0 10px 0" }}>
            You've flown {puzzle.flightTime} hours from {puzzle.departure}{" "}
            {departureCityObj?.flag}
          </h3>

          {puzzle.clues.slice(0, clueLevel).map((c, i) => {
            const isNewClue = i >= initialClueCountRef.current;
            return (
              <p
                key={`clue-${i}`}
                className={isNewClue ? "clue-item" : ""}
                style={{ margin: "6px 0", fontSize: 14.5, color: "#1e293b" }}
              >
                ✈️ {c}
              </p>
            );
          })}
        </div>

        <div style={styles.guessList}>
          {guesses.map((g, i) => (
            <div key={`${g.name}-${i}`} style={styles.guessRow}>
              <div style={styles.guessBox}>
                {g.name}, {g.country} {g.flag}
              </div>

              {/* CONTINENT TILE */}
              <div
                className={g.revealStage === 1 ? "flip-active" : ""}
                style={{
                  ...styles.infoBox,
                  background: getTileBg(1, g.revealStage, g.continentStatus),
                  color: "#1e293b"
                }}
              >
                Continent
              </div>

              {/* COUNTRY TILE */}
              <div
                className={g.revealStage === 2 ? "flip-active" : ""}
                style={{
                  ...styles.infoBox,
                  background: getTileBg(2, g.revealStage, g.countryStatus),
                  color: "#1e293b"
                }}
              >
                Country
              </div>

              {/* CITY TILE */}
              <div
                className={g.revealStage === 3 ? "flip-active" : ""}
                style={{
                  ...styles.infoBox,
                  background: getTileBg(3, g.revealStage, g.cityStatus),
                  color: "#1e293b"
                }}
              >
                City
              </div>
            </div>
          ))}
        </div>

        {!gameOver && (
          <div style={styles.wrapper}>
            <input
              value={guess}
              onChange={(e) => handleInput(e.value ? e.value : e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Type a city..."
              style={styles.input}
              autoCorrect="off"
              autoComplete="one-time-code"
              autoCapitalize="words"
              spellCheck={false}
              name="guess-input-no-autofill"
              id="guess-input-no-autofill"
            />

            {filtered.length > 0 && (
              <div style={styles.dropdown}>
                {filtered.map((c, idx) => (
                  <div
                    key={`${c.name}-${c.country}-${idx}`}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      submitGuess(c);
                    }}
                    style={styles.item}
                  >
                    {c.flag} {c.name}, {c.country}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        <p style={styles.status}>{status}</p>
      </div>

      {game.gameOver && (
        <div style={{ display: "flex", justifyContent: "center", padding: "16px", position: "relative", zIndex: 1 }}>
          <button style={styles.endGameBtn} onClick={goToResults}>
            See Results
          </button>
        </div>
      )}
    </div>
  );
}

// =========================
// STYLES
// =========================

const styles = {
  page: {
    textAlign: "center",
    paddingTop: 70,
    paddingBottom: 40,
    fontFamily: "'Inter', sans-serif",
    background: "#deecfc",
    minHeight: "100dvh",
    width: "100%",
    position: "relative",
    overflowY: "auto",
    overflowX: "hidden",
    WebkitOverflowScrolling: "touch",
    boxSizing: "border-box"
  },
  loading: {
    paddingTop: 100,
    textAlign: "center",
    fontFamily: "sans-serif",
    fontSize: 22,
    fontWeight: 700,
    color: "#334155",
    position: "relative",
    zIndex: 1
  },

  wrapper: {
    position: "relative",
    width: "100%",
    maxWidth: 280,
    margin: "0 auto"
  },
  input: {
    padding: 12,
    width: "100%",
    fontSize: 16,
    boxSizing: "border-box",
    borderRadius: 10,
    border: "2px solid #000000",
    outline: "none"
  },
  dropdown: {
    position: "absolute",
    top: 48,
    left: 0,
    right: 0,
    maxHeight: 200,
    overflowY: "auto",
    background: "white",
    border: "2px solid #000000",
    borderRadius: 10,
    boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
    zIndex: 10,
    WebkitOverflowScrolling: "touch"
  },
  item: {
    padding: "10px 12px",
    cursor: "pointer",
    textAlign: "left",
    borderBottom: "1px solid #f0f0f0"
  },
  status: {
    marginTop: 20,
    fontWeight: "bold"
  },
  guessList: {
    display: "flex",
    flexDirection: "column",
    gap: 10,
    margin: "20px auto",
    width: "100%",
    maxWidth: 420,
    boxSizing: "border-box"
  },
  guessRow: {
    display: "flex",
    gap: 6,
    alignItems: "center",
    width: "100%",
    boxSizing: "border-box"
  },
  guessBox: {
    flex: 1.4,
    padding: "10px",
    background: "#fff",
    borderRadius: 12,
    boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
    textAlign: "left",
    border: "1px solid #cbd5e1",
    fontSize: 13,
    boxSizing: "border-box",
    lineHeight: "1.3",
    wordBreak: "break-word"
  },
  infoBox: {
    flex: 1,
    minWidth: 0,
    padding: "0 2px",
    height: 48,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: 12,
    fontSize: 11,
    fontWeight: "bold",
    boxSizing: "border-box",
    textAlign: "center",
    wordBreak: "break-word",
    perspective: 600
  },
  container: {
    position: "relative",
    zIndex: 1,
    width: "100%",
    maxWidth: 520,
    margin: "0 auto",
    padding: "0 16px 20px 16px",
    boxSizing: "border-box"
  },

  /* CLUE CARD WITH BOLD BLACK BORDER MATCHING SCREENSHOT */
  card: {
    background: "#ffffff",
    padding: "20px",
    margin: "5px 0 20px 0",
    width: "100%",
    boxSizing: "border-box",
    borderRadius: 20,
    boxShadow: "0 6px 16px rgba(15,23,42,0.12)"
  },

  endGameBtn: {
    marginTop: 10,
    padding: "14px",
    borderRadius: 12,
    background: "#6ca965",
    color: "white",
    border: "none",
    fontSize: 16,
    fontWeight: "bold",
    cursor: "pointer",
    width: "200px"
  }
};